/**
 * BottomSheet — Responsive modal wrapper.
 * Mobile (<768px): slides up from bottom (Shadcn Drawer).
 * Desktop (>=768px): renders as centered Dialog.
 *
 * @description Replaces centered modals on mobile across the app.
 * Uses Shadcn Drawer (vaul) on mobile, Shadcn Dialog on desktop.
 */
import { type ReactNode, useEffect, useRef, useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerClose,
  DrawerDescription,
} from '@/components/ui/drawer';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogDescription,
} from '@/components/ui/dialog';
import { X } from 'lucide-react';

/** Props for BottomSheet */
interface BottomSheetProps {
  /** Whether the sheet is open */
  open: boolean;
  /** Called when the sheet should close */
  onClose: () => void;
  /** Title displayed in the sheet header */
  title?: string;
  /** Content rendered inside the sheet */
  children: ReactNode;
  /** Optional snap points for variable height (mobile only) */
  snapPoints?: number[];
}

/**
 * Hook to detect mobile viewport (<768px).
 * @returns Whether the viewport is mobile-sized.
 *
 * [TT-EDITOR-1 S4] EXPORTED, not copied: the timetable grid's drag (D-1: off at phone width) keys on
 * the SAME breakpoint this sheet uses to switch between its drawer and its dialog, so "phone width"
 * means one thing on that screen. The repo already carries ~19 private copies of this hook; S4 adds
 * none.
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return isMobile;
}

/**
 * BottomSheet — Adaptive modal component.
 *
 * @param props - BottomSheetProps
 * @returns Drawer on mobile, Dialog on desktop.
 */
export function BottomSheet({
  open,
  onClose,
  title,
  children,
  snapPoints,
}: BottomSheetProps): JSX.Element {
  const isMobile = useIsMobile();
  const contentRef = useRef<HTMLDivElement>(null);

  // [BF-4c] Focus management to silence aria-hidden retained-focus warnings.
  // On close, blur any element focused inside the sheet before vaul/radix marks
  // the wrapper aria-hidden; on open, move focus into the content (off the trigger).
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      const active = document.activeElement;
      if (active instanceof HTMLElement && contentRef.current?.contains(active)) {
        active.blur();
      }
      onClose();
    }
  };
  const handleOpenAutoFocus = (e: Event) => {
    e.preventDefault();
    contentRef.current?.focus();
  };

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={handleOpenChange}
        snapPoints={snapPoints}
      >
        <DrawerContent
          ref={contentRef}
          onOpenAutoFocus={handleOpenAutoFocus}
          className="outline-none sm:mx-auto sm:max-w-[480px]"
        >
          <DrawerDescription className="sr-only">{title ?? 'Sheet content'}</DrawerDescription>
          {/* Header row: centered title + an always-present close X (renders even without a title) */}
          <div className="relative flex items-center justify-center min-h-[52px] border-b border-[var(--border)]" style={{ paddingLeft: 24, paddingRight: 24 }}>
            <DrawerTitle className={title ? 'text-center font-heading text-[17px] font-semibold text-[var(--t1)]' : 'sr-only'}>
              {title ?? 'Sheet'}
            </DrawerTitle>
            <DrawerClose asChild>
              <button
                aria-label="Close"
                className="absolute right-4 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-xl border border-[var(--border)] bg-[var(--surface-2)] text-[var(--t2)]"
              >
                <X size={18} />
              </button>
            </DrawerClose>
          </div>
          {/* Body scrolls; bottom padding clears the fixed bottom nav so the last control is never under it */}
          <div className="flex-1 min-h-0 overflow-y-auto scroll-container" style={{ paddingLeft: 24, paddingRight: 24, paddingTop: 20, paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}>
            {children}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
    >
      <DialogContent
        ref={contentRef}
        onOpenAutoFocus={handleOpenAutoFocus}
        className="max-h-[85vh] overflow-hidden sm:max-w-lg"
      >
        <DialogDescription className="sr-only">{title ?? 'Sheet content'}</DialogDescription>
        {title && (
          <DialogHeader className="border-b border-[var(--border,#E4E2F0)] pb-3">
            <DialogTitle className="font-heading text-base font-bold text-[var(--t1,#0D0B1E)]">
              {title}
            </DialogTitle>
          </DialogHeader>
        )}
        <div className="overflow-y-auto pt-3 scroll-container">{children}</div>
      </DialogContent>
    </Dialog>
  );
}

export default BottomSheet;